import type { Translations } from "@/i18n";
import { parseDriverRef, resolveRefDisplay } from "@/lib/fleet/driver-ref";
import { formatVehicleSelectLabel } from "@/lib/fleet/vehicle-select";
import type { Notification, Vehicle } from "@/types/fleet";

/**
 * The live `events` a vehicle update carries (`docs/Events.MD`), and how each one reads as a
 * notification. Pure functions so the toast, the notifications page and the sidebar badge all
 * describe an event identically.
 */

export const VEHICLE_EVENT_TYPES = [
  "parking_enter",
  "parking_exit",
  "go_zone_enter",
  "go_zone_exit",
  "nogo_zone_enter",
  "nogo_zone_exit",
  "driver_identified",
  "driver_removed",
  "driver_changed",
  "started",
  "stopped",
  "speeding_start",
  "speeding_end",
  "street_speeding_start",
  "street_speeding_end",
  "refuel",
  "missing_fuel",
  "overheat",
] as const;

export type KnownVehicleEventType = (typeof VEHICLE_EVENT_TYPES)[number];

/**
 * Upstream may add types before the app learns their names, so an event type is any string —
 * unknown ones still toast (preferences default to on) and render with their raw name.
 */
export type VehicleEventType = string;

export type NotificationCategory = "zone" | "driver" | "engine" | "speed" | "fuel";

/** Display order for the settings groups, as the spec prescribes. */
export const CATEGORY_ORDER: readonly NotificationCategory[] = ["zone", "driver", "engine", "speed", "fuel"];

/**
 * Used only until the preferences response arrives; after that the server's own `categories`
 * drive the settings screen, so new types appear there without a client release.
 */
export const DEFAULT_EVENT_CATEGORIES: Record<NotificationCategory, VehicleEventType[]> = {
  zone: ["parking_enter", "parking_exit", "go_zone_enter", "go_zone_exit", "nogo_zone_enter", "nogo_zone_exit"],
  driver: ["driver_identified", "driver_removed", "driver_changed"],
  engine: ["started", "stopped", "overheat"],
  speed: ["speeding_start", "speeding_end", "street_speeding_start", "street_speeding_end"],
  fuel: ["refuel", "missing_fuel"],
};

export interface ParsedVehicleEvent {
  type: VehicleEventType;
  /** Everything after the first `:` — a zone id, a driver reference, a speed limit — or null. */
  arg: string | null;
}

/**
 * Splits on the *first* colon only: a driver event's value is itself tagged (`driver_changed:id:5`),
 * so a plain `split(":")` would cut the reference in half.
 */
export function parseVehicleEvent(raw: string): ParsedVehicleEvent | null {
  const value = raw.trim();
  if (!value) return null;

  const colon = value.indexOf(":");
  if (colon === -1) return { type: value, arg: null };

  const type = value.slice(0, colon);
  const arg = value.slice(colon + 1).trim();
  return type ? { type, arg: arg || null } : null;
}

const EVENT_SEVERITY: Partial<Record<VehicleEventType, Notification["type"]>> = {
  nogo_zone_enter: "alert",
  speeding_start: "alert",
  street_speeding_start: "alert",
  missing_fuel: "alert",
  overheat: "alert",
  nogo_zone_exit: "warning",
  go_zone_exit: "warning",
};

export function vehicleEventSeverity(type: VehicleEventType): Notification["type"] {
  return EVENT_SEVERITY[type] ?? "info";
}

export function vehicleEventLabel(type: VehicleEventType, t: Translations): string {
  return (t.vehicleEvents.types as Record<string, string>)[type] ?? type;
}

const ZONE_EVENT_TYPES = new Set<VehicleEventType>(DEFAULT_EVENT_CATEGORIES.zone);
const DRIVER_EVENT_TYPES = new Set<VehicleEventType>(DEFAULT_EVENT_CATEGORIES.driver);
const SPEED_EVENT_TYPES = new Set<VehicleEventType>(DEFAULT_EVENT_CATEGORIES.speed);
const FUEL_EVENT_TYPES = new Set<VehicleEventType>(DEFAULT_EVENT_CATEGORIES.fuel);

/**
 * Events only a fuel sensor can raise. Neither toasted nor offered in the settings for a client
 * without the `fuel` feature (`lib/fleet/client-features.ts`).
 */
export function isFuelEventType(type: VehicleEventType): boolean {
  return FUEL_EVENT_TYPES.has(type);
}

const HIDDEN_EVENT_TYPES = new Set<VehicleEventType>([
  "started",
  "stopped",
  "speeding_end",
  "street_speeding_end",
]);

/**
 * Engine on/off fires on every trip and a speeding end only closes an alert already shown — not
 * worth a pop-up. Never toasted (healed ends included) and not offered in the settings; upstream
 * still stores them, so the notifications history lists them.
 */
export function isHiddenEventType(type: VehicleEventType): boolean {
  return HIDDEN_EVENT_TYPES.has(type);
}

export interface VehicleEventContext {
  t: Translations;
  vehiclesById: Map<string, Vehicle>;
  zoneNamesById: Map<string, string>;
  driverNamesById: Map<string, string>;
}

/** What the telemetry point that raised the event said, where it matters for the description. */
export interface VehicleEventPoint {
  /** km/h. */
  speed?: number;
  /**
   * Names upstream saved with a stored notification (`detail.*` in `ClientNotification.php`). Used
   * only when the cached lists no longer know the id: a deleted zone, vehicle or driver.
   */
  fallbackNames?: { vehicle?: string; zone?: string; driver?: string };
}

export interface VehicleEventDescription {
  title: string;
  /** Vehicle and detail joined, for places that show one line of prose. */
  message: string;
  vehicleName: string;
  /** The zone / driver / speed the event names, on its own; null when it names nothing. */
  detail: string | null;
}

/** `97 / 90 km/h` when both are known, otherwise whichever half is. */
function describeSpeed(limit: string | null, speed: number | undefined, t: Translations): string | null {
  const current = typeof speed === "number" && Number.isFinite(speed) ? Math.round(speed) : null;
  if (current !== null && limit) return `${current} / ${limit} km/h`;
  if (current !== null) return `${current} km/h`;
  if (limit) return `${t.vehicleEvents.limit} ${limit} km/h`;
  return null;
}

/** The sentence under the title: which vehicle, and the zone / driver / speed the event names. */
export function describeVehicleEvent(
  vehicleId: string,
  event: ParsedVehicleEvent,
  context: VehicleEventContext,
  point: VehicleEventPoint = {},
): VehicleEventDescription {
  const { t, vehiclesById } = context;

  const vehicle = vehiclesById.get(vehicleId);
  const vehicleName = vehicle
    ? formatVehicleSelectLabel(vehicle)
    : point.fallbackNames?.vehicle || `${t.vehicleEvents.vehicle} #${vehicleId}`;
  const detail = vehicleEventDetail(event, context, point);

  return {
    title: vehicleEventLabel(event.type, t),
    message: detail ? `${vehicleName} · ${detail}` : vehicleName,
    vehicleName,
    detail,
  };
}

/**
 * What the event names, on its own: zone name, driver, `97 / 90 km/h`, `12.5 L`, `104 °C`. Shared by the toast, the
 * live map callout and the vehicle list, which all show it beside their own vehicle label.
 */
export function vehicleEventDetail(
  event: ParsedVehicleEvent,
  context: Pick<VehicleEventContext, "t" | "zoneNamesById" | "driverNamesById">,
  point: VehicleEventPoint = {},
): string | null {
  const { t, zoneNamesById, driverNamesById } = context;
  let detail: string | null = null;
  // Speed first: its limit is optional, but the current speed alone is still worth showing.
  if (SPEED_EVENT_TYPES.has(event.type)) {
    detail = describeSpeed(event.arg, point.speed, t);
  } else if (event.arg) {
    if (ZONE_EVENT_TYPES.has(event.type)) {
      detail =
        zoneNamesById.get(event.arg) ??
        (point.fallbackNames?.zone || `${t.vehicleEvents.zone} #${event.arg}`);
    } else if (DRIVER_EVENT_TYPES.has(event.type)) {
      const ref = parseDriverRef(event.arg);
      if (ref) {
        const display = resolveRefDisplay(ref, driverNamesById);
        detail =
          display.kind === "unknown" && point.fallbackNames?.driver
            ? point.fallbackNames.driver
            : display.text;
      }
    } else if (FUEL_EVENT_TYPES.has(event.type)) {
      // Liters added or missing, already rounded to one decimal upstream.
      detail = `${event.arg} L`;
    } else if (event.type === "overheat") {
      detail = `${event.arg} °C`;
    } else {
      detail = event.arg;
    }
  }
  return detail;
}
